import React from 'react';
import { 
  TableRow, 
  TableCell, 
  Stack, 
  Skeleton 
} from '@mui/material';

interface Props {
  rows?: number;
}

const NonEditableForecastSkeleton: React.FC<Props> = ({ rows = 12 }) => {
  return (
    <>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <React.Fragment key={rowIndex}>
          <TableRow
            sx={{
              backgroundColor: "#FFFFFF",
              "& > *": {
                borderBottom: "1px solid rgba(224, 224, 224, 1) !important",
              },
              height: 35,
            }}
          >
            {/* Month Column - Sticky Left */}
            <TableCell
              align="left"
              sx={{
                backgroundColor: "#ffffff",
                padding: "0px 8px !important",
                position: "sticky",
                left: 0,
                zIndex: 2,
              }}
            >
              <Stack direction="row" alignItems="center" gap={0.5}>
                <Skeleton variant="text" width={20} height={20} />
                <Skeleton variant="text" width={60} height={20} />
              </Stack>
            </TableCell>

            {/* NRPU - Sticky */}
            <TableCell
              align="center"
              sx={{
                backgroundColor: "#ffffff",
                padding: "0px 8px !important",
                position: "sticky",
                left: "109px",
                zIndex: 3,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                <Skeleton variant="text" width={50} height={20} />
              </Stack>
            </TableCell>

            {/* Growth Target - Sticky */}
            <TableCell
              align="center"
              sx={{
                backgroundColor: "#ffffff",
                padding: "0px 8px !important",
                position: "sticky",
                left: "196px",
                zIndex: 3,
              }}
            >
              <Skeleton variant="text" width={40} height={20} />
            </TableCell>

            {/* Avg Net Rev Per Active User - Sticky */}
            <TableCell
              align="center"
              sx={{
                backgroundColor: "#ffffff",
                padding: "0px 8px !important",
                position: "sticky",
                left: "337px",
                zIndex: 3,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                <Skeleton variant="text" width={50} height={20} />
              </Stack>
            </TableCell>

            {/* CPA PV - Sticky */}
            <TableCell
              align="center"
              sx={{
                backgroundColor: "#ffffff",
                padding: "0px 8px !important",
                position: "sticky",
                left: "566px",
                zIndex: 3,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                <Skeleton variant="text" width={50} height={20} />
              </Stack>
            </TableCell>

            {/* CVR - Sticky */}
            <TableCell
              align="center"
              sx={{
                backgroundColor: "#ffffff",
                padding: "0px 8px !important",
                position: "sticky",
                left: "664px",
                zIndex: 3,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                <Skeleton variant="text" width={40} height={20} />
              </Stack>
            </TableCell>

            {/* Profit - Sticky with Border */}
            <TableCell
              align="center"
              sx={{
                backgroundColor: "#ffffff",
                padding: "0px 8px !important",
                position: "sticky",
                left: "741px",
                zIndex: 3,
                borderRight: "1px solid #e0e0e0",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                <Skeleton variant="text" width={50} height={20} />
              </Stack>
            </TableCell>

            {/* Active Users */}
            <TableCell
              align="center"
              sx={{
                backgroundColor: "#F5F5F5",
                padding: "0px 8px !important",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                <Skeleton variant="text" width={50} height={20} />
              </Stack>
            </TableCell>

            {/* Old Active Users */}
            <TableCell
              align="center"
              sx={{
                backgroundColor: "#F5F5F5",
                padding: "0px 8px !important",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                <Skeleton variant="text" width={50} height={20} />
              </Stack>
            </TableCell>

            {/* Acquired Active Users */}
            <TableCell
              align="center"
              sx={{
                backgroundColor: "#F5F5F5",
                padding: "0px 8px !important",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                <Skeleton variant="text" width={50} height={20} />
              </Stack>
            </TableCell>

            {/* FTD */}
            <TableCell
              align="center"
              sx={{
                backgroundColor: "#F5F5F5",
                padding: "0px 8px !important",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                <Skeleton variant="text" width={50} height={20} />
              </Stack>
            </TableCell>

            {/* Net Revenue */}
            <TableCell
              align="center"
              sx={{
                backgroundColor: "#F5F5F5",
                padding: "0px 8px !important",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                <Skeleton variant="text" width={50} height={20} />
              </Stack>
            </TableCell>

            {/* RS */}
            <TableCell
              align="center"
              sx={{
                backgroundColor: "#F5F5F5",
                padding: "0px 8px !important",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                <Skeleton variant="text" width={50} height={20} />
              </Stack>
            </TableCell>

            {/* RS PV */}
            <TableCell
              align="center"
              sx={{
                backgroundColor: "#F5F5F5",
                padding: "0px 8px !important",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                <Skeleton variant="text" width={50} height={20} />
              </Stack>
            </TableCell>

            {/* CPA */}
            <TableCell
              align="center"
              sx={{
                backgroundColor: "#F5F5F5",
                padding: "0px 8px !important",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                <Skeleton variant="text" width={50} height={20} />
              </Stack>
            </TableCell>

            {/* Total Income */}
            <TableCell
              align="center"
              sx={{
                backgroundColor: "#F5F5F5",
                padding: "0px 8px !important",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                <Skeleton variant="text" width={50} height={20} />
              </Stack>
            </TableCell>

            {/* Clicks */}
            <TableCell
              align="center"
              sx={{
                backgroundColor: "#F5F5F5",
                padding: "0px 8px !important",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                <Skeleton variant="text" width={50} height={20} />
              </Stack>
            </TableCell>

            {/* EPC CPA */}
            <TableCell
              align="center"
              sx={{
                backgroundColor: "#F5F5F5",
                padding: "0px 8px !important",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                <Skeleton variant="text" width={50} height={20} />
              </Stack>
            </TableCell>

            {/* EPC RS */}
            <TableCell
              align="center"
              sx={{
                backgroundColor: "#F5F5F5",
                padding: "0px 8px !important",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                <Skeleton variant="text" width={50} height={20} />
              </Stack>
            </TableCell>

            {/* EPC */}
            <TableCell
              align="center"
              sx={{
                backgroundColor: "#F5F5F5",
                padding: "0px 8px !important",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                <Skeleton variant="text" width={50} height={20} />
              </Stack>
            </TableCell>

            {/* CPC */}
            <TableCell
              align="center"
              sx={{
                backgroundColor: "#F5F5F5",
                padding: "0px 8px !important",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                <Skeleton variant="text" width={50} height={20} />
              </Stack>
            </TableCell>

            {/* CAC */}
            <TableCell
              align="center"
              sx={{
                backgroundColor: "#F5F5F5",
                padding: "0px 8px !important",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                <Skeleton variant="text" width={50} height={20} />
              </Stack>
            </TableCell>

            {/* Spend */}
            <TableCell
              align="center"
              sx={{
                backgroundColor: "#F5F5F5",
                padding: "0px 8px !important",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                <Skeleton variant="text" width={50} height={20} />
              </Stack>
            </TableCell>

            {/* Avg RS Deal % */}
            <TableCell
              align="center"
              sx={{
                backgroundColor: "#FFFFFF",
                padding: "0px 8px !important",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                <Skeleton variant="text" width={40} height={20} />
              </Stack>
            </TableCell>

            {/* Churn Rate */}
            <TableCell
              align="center"
              sx={{
                backgroundColor: "#FFFFFF",
                padding: "0px 8px !important",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                <Skeleton variant="text" width={40} height={20} />
              </Stack>
            </TableCell>

            {/* BR to NG Active RS Ratio */}
            <TableCell
              align="center"
              sx={{
                backgroundColor: "#FFFFFF",
                padding: "0px 8px !important",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                <Skeleton variant="text" width={40} height={20} />
              </Stack>
            </TableCell>

            {/* Ratio Between Acquired to FTD */}
            <TableCell
              align="center"
              sx={{
                backgroundColor: "#FFFFFF",
                padding: "0px 8px !important",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                <Skeleton variant="text" width={40} height={20} />
              </Stack>
            </TableCell>
          </TableRow>
        </React.Fragment>
      ))}
    </>
  );
};

export default NonEditableForecastSkeleton;
